import { useEffect } from 'react'
import { createPortal } from 'react-dom'

import { useTage } from '../../hooks/queries'
import { fehlertext } from '../../api/client'
import { tagLabel } from '../../lib/format'
import { versatzDesGeraets } from '../../lib/ortszeit'
import { XIcon } from '../../components/Icons'
import { useTexte } from '../../texte'
import { useEscape } from '../../hooks/useEscape'

/**
 * "Zu einem Tag springen" (1.19.0): the days of this chat, one tap each.
 *
 * Opened from a day divider in the chat -- the divider is the place a
 * person already looks at when they wonder "where is the fifth of
 * September". A sheet on top of the page (portal at `document.body`), the
 * days in the **device's** clock like the dividers themselves, the day
 * tapped on marked as current, and a tap on a day hands its first message
 * to the chat, which jumps there the way it jumps from a search hit.
 *
 * The server decides which days there are: every count went through
 * `Visibility`, a reader's list carries their own answers and never
 * another reader's.
 */
export function Tageswahl({
  tripId,
  withUser,
  aktuell,
  onWahl,
  onSchliessen,
}: {
  tripId: number
  withUser?: number
  /** The day of the divider that opened the sheet, as `YYYY-MM-DD`. */
  aktuell: string
  onWahl: (firstId: number) => void
  onSchliessen: () => void
}) {
  const texte = useTexte()
  const t = texte.chat.tage
  const tage = useTage(tripId, withUser, versatzDesGeraets(), true)

  useEscape(onSchliessen)
  useEffect(() => {
    const feld = document.activeElement
    if (feld instanceof HTMLElement) feld.blur()
  }, [])

  const liste = [...(tage.data?.days ?? [])].reverse()

  return createPortal(
    <div className="bogen bogen--obenauf" role="dialog" aria-modal="true" aria-label={t.titel} data-tageswahl="">
      <div className="bogen__blatt">
        <div className="bogen__kopf">
          <h3 className="bogen__titel">{t.titel}</h3>
          <button type="button" className="icon-button" onClick={onSchliessen} aria-label={texte.allgemein.schliessen} title={texte.allgemein.schliessen}>
            <XIcon size={18} />
          </button>
        </div>
        {tage.isError ? (
          <p className="bogen__problem" role="alert">{fehlertext(tage.error)}</p>
        ) : tage.data === undefined ? (
          <p className="bogen__hinweis">{texte.allgemein.laeuft}</p>
        ) : liste.length === 0 ? (
          <p className="bogen__hinweis">{t.keine}</p>
        ) : (
          <ul className="tageswahl__liste">
            {liste.map((tag) => (
              <li key={tag.day}>
                <button
                  type="button"
                  className="tageswahl__zeile"
                  aria-current={tag.day === aktuell ? 'true' : undefined}
                  data-tag={tag.day}
                  onClick={() => onWahl(tag.first_id)}
                >
                  <span className="tageswahl__tag">{tagLabel(tag.day)}</span>
                  <span className="tageswahl__anzahl">{t.anzahl(tag.count)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>,
    document.body,
  )
}
